/**
 * SovereignScope Design Tokens
 * Contains all design system tokens including colors, typography, spacing, and breakpoints
 */

// Risk Assessment Colors
export const riskColors = {
  critical: '#DC2626', // red-600
  high: '#EA580C', // orange-600
  medium: '#D97706', // amber-600
  low: '#16A34A', // green-600
  unknown: '#6B7280', // gray-500
} as const

// Confidence Level Colors
export const confidenceColors = {
  high: '#059669', // emerald-600
  medium: '#0891B2', // cyan-600
  low: '#DC6712', // orange-700
  insufficient: '#DC2626', // red-600
} as const

// Brand Colors
export const brandColors = {
  primary: '#1E40AF', // blue-700
  secondary: '#7C3AED', // violet-600
  background: '#F8FAFC', // slate-50
  surface: '#FFFFFF',
  textPrimary: '#0F172A', // slate-900
  textSecondary: '#475569', // slate-600
  textMuted: '#64748B', // slate-500
} as const

// Semantic Colors
export const semanticColors = {
  success: '#16A34A', // green-600
  warning: '#D97706', // amber-600
  error: '#DC2626', // red-600
  info: '#0891B2', // cyan-600
} as const

// Neutral Colors
export const neutralColors = {
  white: '#FFFFFF',
  gray50: '#F8FAFC',
  gray100: '#F1F5F9',
  gray200: '#E2E8F0',
  gray300: '#CBD5E1',
  gray400: '#94A3B8',
  gray500: '#64748B',
  gray600: '#475569',
  gray700: '#334155',
  gray800: '#1E293B',
  gray900: '#0F172A',
  black: '#000000',
} as const

// Typography
export const typography = {
  fontFamily: {
    primary:
      '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", "Fira Code", Consolas, "Liberation Mono", Menlo, Courier, monospace',
  },
  fontSize: {
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem', // 48px
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeight: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
} as const

// Spacing System
export const spacing = {
  0: '0',
  1: '0.25rem', // 4px
  2: '0.5rem', // 8px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  8: '2rem', // 32px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
  32: '8rem', // 128px
} as const

// Responsive Breakpoints
export const breakpoints = {
  mobile: 375,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
} as const

// Media queries
export const media = {
  mobile: `(min-width: ${breakpoints.mobile}px)`,
  tablet: `(min-width: ${breakpoints.tablet}px)`,
  desktop: `(min-width: ${breakpoints.desktop}px)`,
  wide: `(min-width: ${breakpoints.wide}px)`,
  // Max-width queries for mobile-first approach
  maxMobile: `(max-width: ${breakpoints.tablet - 1}px)`,
  maxTablet: `(max-width: ${breakpoints.desktop - 1}px)`,
  maxDesktop: `(max-width: ${breakpoints.wide - 1}px)`,
} as const

// Shadow System
export const shadows = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
} as const

// Border Radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  full: '9999px',
} as const

// Z-Index Scale
export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modal: 1040,
  popover: 1050,
  tooltip: 1060,
  toast: 1070,
  overlay: 1080,
} as const

// Animation Durations
export const animation = {
  duration: {
    fast: '150ms',
    base: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  easing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
  },
} as const

// Export all tokens as a single object
export const tokens = {
  colors: {
    risk: riskColors,
    confidence: confidenceColors,
    brand: brandColors,
    semantic: semanticColors,
    neutral: neutralColors,
  },
  typography,
  spacing,
  breakpoints,
  media,
  shadows,
  borderRadius,
  zIndex,
  animation,
} as const

export type Tokens = typeof tokens
export type RiskColor = keyof typeof riskColors
export type ConfidenceColor = keyof typeof confidenceColors
export type BrandColor = keyof typeof brandColors
export type SemanticColor = keyof typeof semanticColors
export type NeutralColor = keyof typeof neutralColors
